import { useEffect } from 'react'
import loadable from '@loadable/component'
import { Route, useLocation, Redirect } from 'react-router-dom'

import { useGlobalState } from '../GlobalContext'
import { UserLoginDetails } from '@truepill/emr-analytics/src/events/interface'

import { Section } from '../../Components/Common/SemanticComponents'
import AuthRoute from './AuthRoute'
import { Roles } from '../../types/enums'
import analyticsTracker, { AnalyticsUtils } from '../Services/analytics'
import { isProvider } from '../Utils'
import { IS_PRODUCTION } from 'src/constants'

const Login = loadable(() => import('../../Components/Login/'))
const AppointmentsList = loadable(
  () => import('../../Components/AppointmentsList')
)

const Consultations = loadable(() => import('../../Components/Consultations'))
const MedOps = loadable(() => import('../../Components/MedOps'))
const ConsultVisit = loadable(() => import('../../Components/Consult'))
const Worklist = loadable(() => import('../../Components/Worklist'))
const TaskManagement = loadable(() => import('../../Components/TaskManagement'))
const PatientUploadId = loadable(() => import('../../Components/PatientUpload'))
const TaskPage = loadable(
  () => import('../../Components/TaskManagement/TaskPage')
)
const Scheduler = loadable(() => import('../../Components/Scheduler'))
const Providers = loadable(() => import('../../Components/Providers'))
const NetworkAdmin = loadable(() => import('../../Components/NetworkAdmin'))
const ProviderDetailsPage = loadable(
  () => import('../../Components/ProviderDetails')
)
const AddProviderPage = loadable(
  () => import('../../Components/ProviderDetails/AddProvider')
)
const AddNetworkAdminPage = loadable(
  () => import('../../Components/NetworkAdmin/AddNetworkAdmin')
)
const NewQuestionnaire = loadable(
  () => import('../../Components/QuestionnaireBuilder/NewQuestionnaire')
)
const EditQuestionnaire = loadable(
  () => import('../../Components/QuestionnaireBuilder/EditQuestionnaire')
)
const AccessDenied = loadable(() => import('../../Components/AccessDenied'))
const ErrorFallback = loadable(
  () => import('../../Components/Common/ErrorFallback')
)
const LoginError = loadable(() => import('../../Components/LoginError'))
const Stats = loadable(() => import('../../Components/Stats'))
const MultipleRoles = loadable(() => import('../../Components/MultipleRoles'))
const OutreachSortingCriteria = loadable(
  () => import('../../Components/OutreachSortingCriteria')
)
const AddTestConsult = loadable(() => import('../../Components/AddTestConsult'))
const PeoplesHealthConfiguration = loadable(
  () => import('../../Components/PeoplesHealthConfiguration')
)

export enum RedirectToAfterRoleSelection {
  SWITCH_ROLE_MODAL = 'switch-role-modal',
  ROLES = 'roles',
  FIVE9_SESSION = 'five9-session',
  MAIN = 'main',
}

// TODO: add method to pull by ID
const RoutesList = {
  auth: [
    {
      id: 'medOps',
      component: MedOps,
      path: '/med-ops',
      exact: true,
    },
    {
      id: 'consult',
      component: ConsultVisit,
      path: '/consult/:consultId',
      exact: true,
    },
    {
      id: 'taskManagement',
      component: TaskManagement,
      path: '/tasks',
      exact: true,
    },
    {
      id: 'taskPage',
      component: TaskPage,
      path: '/task/:consultId/:taskId',
      exact: true,
    },
    {
      id: 'subtaskPage',
      component: TaskPage,
      path: '/task/:consultId/:taskId/subtask/:subtaskId',
      exact: true,
    },
    {
      id: 'schedulerPage',
      component: Scheduler,
      path: '/scheduler/:evaluatorId',
      exact: true,
    },
    {
      id: 'providers',
      component: Providers,
      path: '/providers',
      exact: true,
    },
    {
      id: 'addProvider',
      component: AddProviderPage,
      path: '/providers/add',
      exact: true,
    },
    {
      id: 'providerDetails',
      component: ProviderDetailsPage,
      path: '/provider/:providerId',
      exact: true,
    },
    {
      id: 'networkAdmin',
      component: NetworkAdmin,
      path: '/networkAdmin',
      exact: true,
    },
    {
      id: 'addNetworkAdmin',
      component: AddNetworkAdminPage,
      path: '/networkAdmin/add',
      exact: true,
    },
    {
      id: 'APPOINTMENTS_LIST',
      component: AppointmentsList,
      path: '/appointments',
      exact: true,
    },
    {
      id: 'stats',
      component: Stats,
      path: '/stats',
      exact: true,
    },
    {
      id: 'multipleRoles',
      component: MultipleRoles,
      path: '/roles',
      exact: true,
    },
    {
      id: 'consultation',
      component: Consultations,
      path: '/consultations',
      exact: true,
    },
    {
      id: 'newQuestionnaire',
      component: NewQuestionnaire,
      path: '/questionnaires/add',
      exact: true,
    },
    {
      id: 'editQuestionnaire',
      component: EditQuestionnaire,
      path: '/questionnaire/:questionnaireId',
      exact: true,
    },
    {
      id: 'worklist',
      component: Worklist,
      path: '/worklist',
      exact: true,
    },
    {
      id: 'outreachSortingCriteria',
      component: OutreachSortingCriteria,
      path: '/outreachSortingCriteria',
      exact: true,
    },
    {
      id: 'peoplesHealthConfiguration',
      component: PeoplesHealthConfiguration,
      path: '/peoplesHealthConfiguration',
      exact: true,
    },
    ...(!IS_PRODUCTION
      ? [
          {
            id: 'addTestConsult',
            component: AddTestConsult,
            path: '/addTestConsult',
            exact: true,
          },
        ]
      : []),
  ],
  open: [
    {
      id: 'login',
      component: Login,
      path: '/',
      exact: true,
    },
    {
      id: 'patientUploadId',
      component: PatientUploadId,
      path: '/patient/update-photos',
      exact: true,
    },
    {
      id: 'accessDenied',
      component: AccessDenied,
      path: '/access-denied',
      exact: true,
    },
    {
      id: 'error',
      component: ErrorFallback,
      path: '/error',
      exact: true,
    },
    {
      id: 'loginError',
      component: LoginError,
      path: '/login-error',
      exact: true,
    },
  ],
}

const trackDeviceUsageStats = (whoAmI: Roles) => {
  // Send usage stats for doctor
  if (isProvider(whoAmI)) {
    analyticsTracker.sendProviderStatsEvent({
      deviceType: AnalyticsUtils.getScreenType(),
    })
  }
}

const Routes = () => {
  const { open, auth } = RoutesList
  const {
    isLoggedIn,
    whoAmI,
    userInfo: { user },
  } = useGlobalState()
  const location = useLocation()

  const publicRoutes = open.map(({ id, component, path, exact }) => (
    <Route key={id} path={path} component={component} exact={exact} />
  ))
  const authedRoutes = (
    <>
      {auth.map(({ id, component, path, exact }) => (
        <Section
          key={id}
          maxWidth={component !== Worklist ? '1200px' : '100%'}
          flexWrap
        >
          <AuthRoute component={component} path={path} exact={exact} />
        </Section>
      ))}
    </>
  )

  useEffect(() => {
    if (isLoggedIn && location.pathname === '/') {
      if (user) {
        analyticsTracker.sendUserLoginState({
          username: user,
          timestamp: new Date(),
          authStatus: 'loggedIn' as UserLoginDetails['authStatus'],
        })
      }

      // Send Analytics for doctor stats
      if (whoAmI) {
        trackDeviceUsageStats(whoAmI)
      }
    }
  }, [isLoggedIn, location.pathname, user, whoAmI])

  if (isLoggedIn && location.pathname === '/') {
    // if user is logged in and at root path, force them to redirect to /consultations
    const newSearchParams = new URLSearchParams(window.location.search).get(
      `fwdTo`
    ) as string
    if (newSearchParams) {
      return <Redirect to={newSearchParams} />
    } else {
      return <Redirect to="/consultations" />
    }
  }

  return (
    <>
      {publicRoutes}
      {authedRoutes}
    </>
  )
}

export default Routes
